<script lang="ts" setup>
import type { Ref } from "vue"
import type { IMenuItem } from "@/components/legacy/Menu/menu.interface"
import type { ToastHtmlElement } from "@/types"
import logo from "@brand-logo"
import footerLogo from "@brand-logo-footer"
import { useMutation } from "@tanstack/vue-query"
import { computed, ref } from "vue"
import { useRouter } from "vue-router"
import { useGetEventVault } from "@/api"
import { getBrandFooterUrls } from "@/components/Footer/footer.constants"
import HostNavbarMenu from "@/components/HostNavbar/HostNavbarMenu.vue"
import { Menu } from "@/components/legacy"
import NavMobileMenu from "@/components/Nav/NavMobileMenu.vue"
import { AppLink, Button, Icon, Toast } from "@/components/ui"
import { useTranslation } from "@/composables"
import { EventVaultStatus } from "@/models"
import { useAuthStore } from "@/stores/authStore"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { UserRole } from "@/types"

const authStore = useAuthStore()
const eventStore = useEventVaultStore()

const { data: eventVault } = useGetEventVault(eventStore.eventVaultId)

const { t } = useTranslation()

const isMenuOpen: Ref<boolean> = ref(false)
const isMobileView: Ref<boolean> = ref(window.innerWidth < 768)
const successToast = ref<ToastHtmlElement | null>(null)
const errorToast = ref<ToastHtmlElement | null>(null)

const toggleMenu = () => (isMenuOpen.value = !isMenuOpen.value)

const router = useRouter()

const { mutate } = useMutation({
  mutationFn: () => authStore.logout(),
  onError: () => errorToast.value?.showToast(),
  onSuccess: () => {
    successToast.value?.showToast({
      title: t("success"),
      content: t("host_nav.logout_success"),
      icon: "CircleCheck",
    })

    router.push("/login")
  },
})

const navItemTabIndex = computed(() => {
  return isMenuOpen.value || !isMobileView.value ? "0" : "-1"
})

const canViewEvent = computed(() => Boolean(eventVault.value && eventVault.value.status !== EventVaultStatus.Archived))

const defaultMenuItems = computed<IMenuItem[]>(() => [
  {
    key: "dashboard",
    label: t("host_nav.dashboard"),
    to: "/event/dashboard",
    isHidden: !authStore.isAuthenticated || !authStore.user?.emailConfirmed,
  },
  {
    key: "setup",
    label: t("host_nav.setup"),
    isHidden: !authStore.isAuthenticated || !canViewEvent.value,
    children: [
      {
        key: "event_summary",
        label: t("host_nav.summary"),
        to: `/event/${eventVault.value?.id}`,
      },
      {
        key: "edit_event",
        label: t("host_nav.edit"),
        to: `/event/${eventVault.value?.id}/edit`,
      },
      {
        key: "settings",
        label: t("host_nav.settings"),
        to: `/event/${eventVault.value?.id}/advanced-settings`,
      },
    ],
  },
  {
    key: "event",
    label: t("host_nav.your_event"),
    isHidden: !authStore.isAuthenticated || !canViewEvent.value,
    children: [
      {
        key: "gallery",
        label: t("host_nav.gallery"),
        to: `/guest/${eventVault.value?.id}/gallery`,
      },
      {
        key: "upload",
        label: t("host_nav.upload"),
        to: `/guest/${eventVault.value?.id}/gallery`,
      },
      {
        key: "manage_media",
        label: t("host_nav.manage_media"),
        to: `/event/${eventVault.value?.id}/manage-media`,
      },
      {
        key: "messages",
        label: t("host_nav.messages"),
        to: `/guest/${eventVault.value?.id}/messages`,
      },
      {
        key: "albums",
        label: t("host_nav.albums"),
        to: `/event/${eventVault.value?.id}/albums`,
      },
      {
        key: "guests",
        label: t("host_nav.guest_list"),
        to: `/event/${eventVault.value?.id}/guest-list`,
      },
    ],
  },
  {
    key: "actions",
    label: t("host_nav.actions"),
    isHidden: !authStore.isAuthenticated || !canViewEvent.value,
    children: [
      {
        key: "slideshow",
        label: t("host_nav.slideshow"),
        to: `/event/${eventVault.value?.id}/slideshow`,
      },
      {
        key: "zip",
        label: t("host_nav.download_zip"),
        to: `/event/dashboard?export=${eventVault.value?.id}`,
      },
      {
        key: "print",
        label: t("host_nav.design_templates"),
        to: `/event/${eventVault.value?.id}/#headingFour`,
      },
      {
        key: "qr",
        label: t("host_nav.download_qr"),
        to: `/event/dashboard?links=${eventVault.value?.id}`,
      },
    ],
  },
  {
    key: "affiliate",
    label: t("host_nav.affiliates"),
    href: "https://guestpix.com/affiliate-area-login/",
    isHidden: !authStore.user?.roles?.includes(UserRole.Affiliate),
  },
  {
    key: "help",
    label: t("host_nav.help"),
    href: getBrandFooterUrls().helpCenter,
  },
])

const userMenuItems = computed<IMenuItem[]>(() => [
  {
    key: "account",
    label: t("host_nav.account_settings"),
    to: "/profile",
    isHidden: !authStore.isAuthenticated || !authStore.user?.emailConfirmed,
  },
  {
    key: "logout",
    label: t("host_nav.logout"),
    onClick: () => mutate(),
    variant: "danger",
    dismissOnInteract: true,
    isHidden: !authStore.isAuthenticated || !authStore.user?.emailConfirmed,
  },
])

const allMenuItems = computed(() => {
  return [...defaultMenuItems.value, ...userMenuItems.value]
})
</script>

<template>
  <nav class="gpx-navbar bg-white border-b border-b-black flex items-center justify-center">
    <div class="container flex w-full items-center">
      <AppLink
        to="/event/dashboard" :tabindex="!isMenuOpen ? '0' : '-1'"
        class="gpx-brand overflow-hidden mr-12"
      >
        <img :src="logo" alt="Guestpix Logo" loading="lazy">
      </AppLink>
      <div class="ml-auto block md:hidden">
        <Button :tabindex="navItemTabIndex" variant="flat" @click="toggleMenu">
          <Icon icon="CircleMenu" />
        </Button>
      </div>
      <div class="gpx-nav-items hidden md:block w-full" :class="{ 'gpx-nav-items_open': isMenuOpen }">
        <div class="md:hidden flex items-center justify-between border-b border-b-black py-3">
          <AppLink to="/" data-test="nav-brand" class="gpx-brand-mobile" :tabindex="navItemTabIndex">
            <img :src="logo" alt="Guestpix Logo">
          </AppLink>
          <div class="block md:hidden">
            <Button :tabindex="isMenuOpen ? '0' : '-1'" variant="flat" @click="toggleMenu">
              <span class="sr-only">Close menu</span>
              <Icon icon="Close" size="sm" />
            </Button>
          </div>
        </div>
        <ul class="flex flex-col md:flex-row gap-8 sm:gap-4 lg:gap-8 py-10 px-4 md:p-0">
          <HostNavbarMenu
            v-for="(item, index) in defaultMenuItems" :key="`${item.key}-${index}`"
            :item="item"
          />
          <Menu v-if="authStore.isAuthenticated && authStore.user?.emailConfirmed" class="md:ml-auto" :items="userMenuItems">
            <template #activator="{ toggle }">
              <button @click="toggle">
                <Icon icon="User" />
                <span class="sr-only">Toggle user menu</span>
              </button>
            </template>
          </Menu>
        </ul>
        <div class="flex md:hidden absolute bottom-0 w-full items-center justify-center py-4">
          <img :src="footerLogo" alt="Guestpix Logo" loading="lazy">
        </div>
      </div>
      <transition name="gpx-slide-right">
        <NavMobileMenu v-if="isMenuOpen" :items="allMenuItems" class="md:hidden" @close="isMenuOpen = false" />
      </transition>
    </div>
  </nav>

  <Toast ref="successToast" variant="success" />
  <Toast ref="errorToast" variant="danger">
    <div class="gpx-uploader-toast-content flex flex-col w-full">
      <div class="gpx-uploader-toast-content-title flex flex-row items-center gap-2 mb-2 whitespace-nowrap">
        <Icon icon="FolderCross" />
        <h5 class="text-base md:text-lg mr-4">
          {{ t("something_went_wrong") }}
        </h5>
      </div>
      <p class="font-montserrat text-sm m-0 max-w-[250px]">
        <i18n-t keypath="host_nav.logout_failed" scope="global">
          <template #link>
            <a
              href="mailto:customercare@guestpix.com"
              class="hover:underline focus:underline cursor-pointer text-white" target="_blank"
            >
              {{ t("customer_care_email") }}
            </a>
          </template>
        </i18n-t>
      </p>
    </div>
  </Toast>
</template>

<style scoped>
.gpx-brand {
  max-height: calc(var(--gpx-navbar-height) - 1.5rem);
}

.gpx-navbar img {
  max-height: 56px;
}

.gpx-navbar {
  min-height: var(--gpx-navbar-height);
}

@media (width < 768px) {
  .gpx-nav-items {
    transform: translate(-150%);
    transition: transform 0.5s ease-in-out;
    background-color: #e4d2be;
  }

  .gpx-nav-items_open {
    transform: translateX(0);
    overflow: hidden;
  }
}
</style>
